// Design System variant names. Emitted as `chip--<variant>` BEM modifiers so they
// can never collide with global utility classes like `.error` / `.warning`.
export const CHIP_VARIANT = {
    GRAY: 'gray',
    BRAND: 'brand',
    ERROR: 'error',
    WARNING: 'warning',
    SUCCESS: 'success',
    BLUE_GRAY: 'blue-gray',
    BLUE_LIGHT: 'blue-light',
    BLUE: 'blue',
    INDIGO: 'indigo',
    PURPLE: 'purple',
    PINK: 'pink',
    ORANGE: 'orange',
    MODERN: 'modern',
};

// Pre-DS variant names. These keep emitting the exact bare class names the app
// ships today (`.chip.secondary`, `.chip.lightblue`, …) so existing call sites
// and any CSS targeting those classes are untouched. `dashed` was written for
// SuggestedAttributes and its `.chip.dashed` CSS has always existed — only the
// old class allowlist forgot it, leaving that screen's chip unstyled.
export const LEGACY_CHIP_VARIANT = {
    DEFAULT: 'default',
    SECONDARY: 'secondary',
    LIGHT_BLUE: 'lightblue',
    ORANGE: 'orange',
    PINK: 'pink',
    INDIGO: 'indigo',
    AI: 'ai',
    DASHED: 'dashed',
};

// Accepted but intentionally class-less, matching current production behavior:
// `variant="green"` never made it into the class allowlist, so JobFooter's
// saving/saved chips render it unstyled. Mapping it now would visibly change
// that footer on every job form.
export const INERT_LEGACY_VARIANTS = ['green'];

// Lower-cased two-word DS names resolved back to their kebab-case canonical
// form (callers historically wrote camelCase, e.g. `blueGray`). `lightblue` is
// not here: lower-casing alone lands it on its legacy bare class above.
export const VARIANT_ALIASES = {
    bluegray: CHIP_VARIANT.BLUE_GRAY,
    bluelight: CHIP_VARIANT.BLUE_LIGHT,
};

// Historical camelCase propTypes spellings still used by callers;
// helpers.normalizeVariant lower-cases them into the names above.
export const LEGACY_PROP_SPELLINGS = ['lightBlue', 'blueGray', 'blueLight'];

// `orange`, `pink` and `indigo` predate the DS but are the same hues, so they
// stay on their legacy bare classes; only genuinely new names get modifiers.
export const DS_ONLY_VARIANTS = new Set([
    CHIP_VARIANT.GRAY,
    CHIP_VARIANT.BRAND,
    CHIP_VARIANT.ERROR,
    CHIP_VARIANT.WARNING,
    CHIP_VARIANT.SUCCESS,
    CHIP_VARIANT.BLUE_GRAY,
    CHIP_VARIANT.BLUE_LIGHT,
    CHIP_VARIANT.BLUE,
    CHIP_VARIANT.PURPLE,
    CHIP_VARIANT.MODERN,
]);

export const LEGACY_VARIANT_CLASSES = new Set(Object.values(LEGACY_CHIP_VARIANT));

export const CHIP_SIZE = {
    SM: 'sm',
    MD: 'md',
    LG: 'lg',
};

export const CHIP_SIZES = new Set(Object.values(CHIP_SIZE));

// Single source for the `variant` propTypes allowlist — derived from the maps
// above so a new variant or alias never has to be added in two places.
export const CHIP_VARIANT_PROP_VALUES = [
    ...new Set([
        ...Object.values(CHIP_VARIANT),
        ...Object.values(LEGACY_CHIP_VARIANT),
        ...LEGACY_PROP_SPELLINGS,
        ...INERT_LEGACY_VARIANTS,
    ]),
];

export const OVERFLOW_COUNT_PREFIX = '+';

export const CHIP_ACTIVATION_KEYS = ['Enter', ' '];
